import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Download, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Redo2, Printer, Share2, Link as LinkIcon, Copy, CheckCircle2, MessageCircle, CreditCard, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const ToolBtn = ({ onClick, children, title, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition">{children}</button>
);

function contentToHtml(text) {
  return text.split("\n").map((l) => {
    const s = l.trim();
    if (!s) return "<p><br/></p>";
    if (s === s.toUpperCase() && s.length < 80 && s.length > 0) {
      return `<p style="text-align:center;"><b>${s}</b></p>`;
    }
    return `<p>${s}</p>`;
  }).join("");
}

function htmlToText(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const blocks = tmp.querySelectorAll("p, div");
  const lines = [];
  if (blocks.length === 0) return tmp.innerText;
  blocks.forEach(b => lines.push(b.innerText || ""));
  return lines.join("\n");
}

export default function DocumentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    api.get(`/documents/${id}`).then(r => {
      setDoc(r.data);
      setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = contentToHtml(r.data.content); }, 50);
      // fetch client info for phone
      if (r.data.client_id) api.get(`/clients/${r.data.client_id}`).then(cr => setClient(cr.data.client)).catch(() => {});
    });
    api.get(`/documents/${id}/share`).then(r => setShare(r.data?.token ? r.data : null)).catch(() => {});
  }, [id]);

  const cmd = (c, v=null) => { document.execCommand(c, false, v); editorRef.current?.focus(); };

  const save = async () => {
    setSaving(true);
    try {
      const text = htmlToText(editorRef.current.innerHTML);
      const r = await api.put(`/documents/${id}`, { content: text });
      setDoc(r.data);
      toast.success("Documento salvo");
    } catch(e) { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const download = async (fmt) => {
    await save();
    window.open(`${API}/documents/${id}/export?format=${fmt}`, "_blank");
  };

  const createShare = async () => {
    await save();
    const r = await api.post(`/documents/${id}/share`);
    setShare(r.data);
    setShareOpen(true);
  };
  const shareUrl = share ? `${window.location.origin}/sign/${share.token}` : "";
  const copyShare = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copiado"); };
  const shareWhatsApp = async () => {
    let link = share;
    if (!link) {
      await save();
      const r = await api.post(`/documents/${id}/share`);
      link = r.data; setShare(link); setShareOpen(true);
    }
    const url = `${window.location.origin}/sign/${link.token}`;
    const raw = (client?.telefone || "").replace(/\D/g, "");
    const num = raw.startsWith("55") ? raw : (raw ? "55" + raw : "");
    const text = encodeURIComponent(`Olá ${client?.nome_completo?.split(' ')[0] || ''}! Segue o link para leitura e assinatura do documento: ${url}`);
    window.open(num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
  };

  if (!doc) return <div className="p-8 text-slate-500">Carregando...</div>;

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-5xl mx-auto w-full">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4"/> Voltar
      </button>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">{doc.title}</h1>
          <p className="text-xs text-slate-500 mt-1">Última atualização: {new Date(doc.updated_at).toLocaleString('pt-BR')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={save} disabled={saving} data-testid="btn-save-doc" className="px-4 h-10 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-2 hover:bg-slate-50">
            <Save className="w-4 h-4"/> {saving ? "Salvando..." : "Salvar"}
          </button>
          <button onClick={createShare} data-testid="btn-share" className="px-4 h-10 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-2 hover:bg-slate-50">
            <Share2 className="w-4 h-4"/> Compartilhar
          </button>
          <button onClick={shareWhatsApp} data-testid="btn-share-whatsapp" className="px-4 h-10 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm inline-flex items-center gap-2 hover:bg-emerald-100 transition">
            <MessageCircle className="w-4 h-4"/> WhatsApp
          </button>
          {doc.type === "contrato_honorarios" && (
            <button
              data-testid="btn-charge"
              onClick={async () => {
                try {
                  const r = await api.post(`/documents/${id}/payment-link`);
                  toast.success(`Link de cobrança criado (R$ ${r.data.amount.toFixed(2)})`);
                  navigator.clipboard.writeText(r.data.url);
                  window.open(r.data.url, "_blank");
                } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao criar cobrança"); }
              }}
              className="px-4 h-10 rounded-lg bg-emerald-600 text-white text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
              <DollarSign className="w-4 h-4"/> Cobrar honorários
            </button>
          )}
          <button onClick={() => download("pdf")} data-testid="btn-download-pdf" className="gradient-primary text-white px-4 h-10 rounded-lg text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
            <Download className="w-4 h-4"/> Baixar PDF
          </button>
          <button onClick={() => download("docx")} data-testid="btn-download-docx" className="bg-slate-900 text-white px-4 h-10 rounded-lg text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
            <Download className="w-4 h-4"/> DOCX
          </button>
        </div>
      </div>

      {shareOpen && share && (
        <div className="mb-4 bg-white rounded-2xl border border-slate-200 p-5" data-testid="share-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <LinkIcon className="w-4 h-4 text-[#0f4c5c]"/>
                <div className="font-heading text-base font-semibold">Link público para assinatura</div>
                {share.signed_at && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3"/> Assinado por {share.signer_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input readOnly value={shareUrl} data-testid="share-url"
                  className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono"/>
                <button onClick={copyShare} data-testid="btn-copy-share" className="px-3 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm inline-flex items-center gap-2">
                  <Copy className="w-4 h-4"/> Copiar
                </button>
                <a href={shareUrl} target="_blank" rel="noreferrer" className="px-3 h-10 rounded-lg gradient-primary text-white text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
                  Abrir
                </a>
                <button onClick={shareWhatsApp} data-testid="btn-panel-whatsapp" className="px-3 h-10 rounded-lg bg-emerald-600 text-white text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
                  <MessageCircle className="w-4 h-4"/> WhatsApp
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Envie este link ao cliente. Ele poderá ler e assinar eletronicamente sem precisar de conta.</p>
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(shareUrl)}`}
                  alt="QR" className="w-24 h-24 rounded-lg border border-slate-200 bg-white p-1"
                  data-testid="qr-preview"/>
                <div className="text-xs text-slate-500">
                  <div className="font-medium text-slate-700 mb-1">QR embutido em cada PDF/DOCX gerado</div>
                  O cliente escaneia com o celular e cai na tela de assinatura. Use para colher a assinatura no escritório sem precisar imprimir.
                </div>
              </div>
            </div>
            <button onClick={() => setShareOpen(false)} className="text-slate-400 hover:text-slate-900 text-sm">Fechar</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-t-2xl border border-b-0 border-slate-100 p-2 flex items-center gap-1 sticky top-0 z-10">
        <ToolBtn testid="tb-bold" title="Negrito" onClick={() => cmd("bold")}><Bold className="w-4 h-4"/></ToolBtn>
        <ToolBtn testid="tb-italic" title="Itálico" onClick={() => cmd("italic")}><Italic className="w-4 h-4"/></ToolBtn>
        <ToolBtn testid="tb-underline" title="Sublinhado" onClick={() => cmd("underline")}><Underline className="w-4 h-4"/></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1"/>
        <ToolBtn title="Esquerda" onClick={() => cmd("justifyLeft")}><AlignLeft className="w-4 h-4"/></ToolBtn>
        <ToolBtn title="Centro" onClick={() => cmd("justifyCenter")}><AlignCenter className="w-4 h-4"/></ToolBtn>
        <ToolBtn title="Direita" onClick={() => cmd("justifyRight")}><AlignRight className="w-4 h-4"/></ToolBtn>
        <ToolBtn title="Justificado" onClick={() => cmd("justifyFull")}><AlignJustify className="w-4 h-4"/></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1"/>
        <ToolBtn title="Desfazer" onClick={() => cmd("undo")}><Undo2 className="w-4 h-4"/></ToolBtn>
        <ToolBtn title="Refazer" onClick={() => cmd("redo")}><Redo2 className="w-4 h-4"/></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1"/>
        <ToolBtn title="Imprimir" onClick={() => window.print()}><Printer className="w-4 h-4"/></ToolBtn>
      </div>

      <div className="bg-slate-100 p-6 rounded-b-2xl border border-slate-100">
        <div className="doc-page">
          <div ref={editorRef} contentEditable suppressContentEditableWarning
            className="font-doc min-h-[24cm] outline-none" data-testid="doc-editor"/>
        </div>
      </div>
    </div>
  );
}
